<script setup lang="ts">
  import { computed } from 'vue';
  import {
    TRAINING_TYPE_BADGES,
    TRAINING_TYPE_LABELS,
    type MemberTrainingStatus,
    type TrainingDetail,
    type TrainingDetailTrainee,
  } from '@/entities/Training';
  import { formatCivilDateToPtBR } from '@/shared/lib';
  import { buildTrainingMatrixRevisionOptionLabel } from '../../lib/buildTrainingMatrixRevisionOptionLabel';
  import { TRAINING_EMPTY_VALUE } from '../../lib/trainingEmptyValue';
  import TrainingDocumentsTable from '../TrainingDocumentsTable.vue';
  import TrainingViewTraineesTable from './TrainingViewTraineesTable.vue';
  import TrainingViewInstructorsList from './TrainingViewInstructorsList.vue';
  import TrainingViewOpenInstructorsList from './TrainingViewOpenInstructorsList.vue';
  import TrainingViewCertificateFields from './TrainingViewCertificateFields.vue';
  import TrainingViewDocumentSection from './TrainingViewDocumentSection.vue';

  const props = withDefaults(
    defineProps<{
      training: TrainingDetail;
      showTraineesSection?: boolean;
      isChangingTraineeStatus?: (id: number) => boolean;
      generatingIds?: number[];
    }>(),
    { showTraineesSection: true, isChangingTraineeStatus: () => false, generatingIds: () => [] }
  );

  const emit = defineEmits<{
    'change-trainee-status': [id: number, status: MemberTrainingStatus];
    'upload-certificate': [trainee: TrainingDetailTrainee];
    'view-certificate': [trainee: TrainingDetailTrainee];
    'generate-certificate': [trainee: TrainingDetailTrainee];
  }>();

  /**
   * Each training type composes the tab from its own set of sections. `DEFAULT` is the composition
   * delivered for Onboarding and Alteração de função; `SGQ_PUNCTUAL` is the one the SGQ Pontual
   * prototype asks for; `OPEN` is the one Treinamento Aberto asks for; `SGQ_HOMOLOGATION` swaps
   * "Programação Treinamento" for "Responsável pela aprovação" since this training is created
   * automatically by the document approval flow. New tabbed types get their own case here.
   */
  const sectionsVariant = computed(() => {
    if (props.training.type === 'OPEN_TRAINING') return 'OPEN';
    if (props.training.type === 'SGQ_DOCUMENT_FOCAL') return 'SGQ_PUNCTUAL';
    if (props.training.type === 'SGQ_DOCUMENT_HOMOLOGATION') return 'SGQ_HOMOLOGATION';
    return 'DEFAULT';
  });

  const usesSgqComposition = computed(
    () => sectionsVariant.value === 'SGQ_PUNCTUAL' || sectionsVariant.value === 'SGQ_HOMOLOGATION'
  );

  const typeBadges = computed(() => TRAINING_TYPE_BADGES[props.training.type]);

  const linkedDocument = computed(() => props.training.documents[0] ?? null);

  const matrixRevisionLabel = computed(() => {
    const revision = props.training.matrixRevision;
    return revision ? buildTrainingMatrixRevisionOptionLabel(revision) : TRAINING_EMPTY_VALUE;
  });

  const scheduledDateLabel = computed(() => {
    const date = props.training.scheduling.date;
    return date ? formatCivilDateToPtBR(date) : TRAINING_EMPTY_VALUE;
  });

  const approvalDateLabel = computed(() => {
    const date = props.training.approval?.date;
    return date ? formatCivilDateToPtBR(date) : TRAINING_EMPTY_VALUE;
  });

  const documentRows = computed(() =>
    props.training.documents.map((document) => ({
      id: document.documentRevisionId,
      code: document.prefix,
      name: document.title,
    }))
  );
</script>

<template>
  <div class="flex flex-col rounded-2xl border border-gray-02 bg-white-01">
    <section
      v-if="usesSgqComposition"
      data-testid="view-training-section-type"
      class="flex flex-col gap-4 border-b border-gray-02 px-6 py-8"
    >
      <div class="flex flex-col gap-2">
        <span class="text-xs font-semibold text-gray-07">Tipo de Treinamento</span>
        <div class="flex flex-wrap gap-4">
          <span
            v-for="badge in typeBadges"
            :key="badge"
            data-testid="view-training-type-badge"
            class="w-fit rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3.5 text-sm text-gray-07"
          >
            {{ badge }}
          </span>
        </div>
      </div>
    </section>

    <section
      v-else
      data-testid="view-training-section-type"
      class="flex flex-col gap-4 border-b border-gray-02 px-6 py-8"
    >
      <div class="flex flex-wrap gap-4">
        <div class="flex flex-col gap-2">
          <span class="text-xs font-semibold text-gray-07">Tipo de Treinamento</span>
          <span
            data-testid="view-training-type"
            class="w-fit rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3.5 text-sm text-gray-07"
          >
            {{ TRAINING_TYPE_LABELS[training.type] }}
          </span>
        </div>
        <div
          v-if="sectionsVariant === 'DEFAULT'"
          class="flex flex-col gap-2"
        >
          <span class="text-xs font-semibold text-gray-07">Matriz de Treinamento</span>
          <span
            data-testid="view-training-matrix-revision"
            class="w-fit rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3.5 text-sm text-gray-07"
          >
            {{ matrixRevisionLabel }}
          </span>
        </div>
      </div>
    </section>

    <TrainingViewDocumentSection
      v-if="usesSgqComposition"
      :document="linkedDocument"
    />

    <section
      v-if="!usesSgqComposition"
      data-testid="view-training-section-title"
      class="flex flex-col gap-4 border-b border-gray-02 px-6 py-8"
    >
      <h2 class="text-sm font-semibold text-gray-07">
        Título
      </h2>
      <span
        class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3.5 text-sm text-gray-07"
      >
        {{ training.title }}
      </span>
    </section>

    <section
      v-if="sectionsVariant !== 'SGQ_HOMOLOGATION'"
      data-testid="view-training-section-scheduling"
      class="flex flex-col gap-4 border-b border-gray-02 px-6 py-8"
    >
      <h2 class="text-sm font-semibold text-gray-07">
        Programação Treinamento
      </h2>
      <div class="flex flex-wrap gap-4">
        <div class="flex w-full flex-col gap-2 md:w-67">
          <span class="text-xs font-semibold text-gray-07">Responsável pela Programação</span>
          <span
            data-testid="view-training-scheduling-responsible"
            class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
          >
            {{ training.scheduling.responsible?.name ?? TRAINING_EMPTY_VALUE }}
          </span>
        </div>
        <div class="flex w-full flex-col gap-2 md:w-80">
          <span class="text-xs font-semibold text-gray-07">Cargo</span>
          <span
            data-testid="view-training-scheduling-position"
            class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
          >
            {{ training.scheduling.position?.name ?? TRAINING_EMPTY_VALUE }}
          </span>
        </div>
        <div class="flex w-full flex-col gap-2 md:w-45">
          <span class="text-xs font-semibold text-gray-07">Data da programação</span>
          <span
            data-testid="view-training-scheduling-date"
            class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
          >
            {{ scheduledDateLabel }}
          </span>
        </div>
      </div>
    </section>

    <section
      v-if="sectionsVariant === 'SGQ_HOMOLOGATION'"
      data-testid="view-training-section-approval"
      class="flex flex-col gap-4 border-b border-gray-02 px-6 py-8"
    >
      <h2 class="text-sm font-semibold text-gray-07">
        Responsável pela aprovação
      </h2>
      <div class="flex flex-wrap gap-4">
        <div class="flex w-full flex-col gap-2 md:w-67">
          <span class="text-xs font-semibold text-gray-07">Responsável pela aprovação do treinamento</span>
          <span
            data-testid="view-training-approval-responsible"
            class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
          >
            {{ training.approval?.responsible?.name ?? TRAINING_EMPTY_VALUE }}
          </span>
        </div>
        <div class="flex w-full flex-col gap-2 md:w-80">
          <span class="text-xs font-semibold text-gray-07">Cargo</span>
          <span
            data-testid="view-training-approval-position"
            class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
          >
            {{ training.approval?.position?.name ?? TRAINING_EMPTY_VALUE }}
          </span>
        </div>
        <div class="flex w-full flex-col gap-2 md:w-45">
          <span class="text-xs font-semibold text-gray-07">Data da aprovação</span>
          <span
            data-testid="view-training-approval-date"
            class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
          >
            {{ approvalDateLabel }}
          </span>
        </div>
      </div>
    </section>

    <section
      v-if="!usesSgqComposition && showTraineesSection"
      data-testid="view-training-section-trainees"
      class="flex flex-col gap-4 border-b border-gray-02 px-6 py-8"
    >
      <h2 class="text-sm font-semibold text-gray-07">
        Colaborador Treinado
      </h2>
      <TrainingViewTraineesTable
        :trainees="training.trainees"
        :is-changing-status="isChangingTraineeStatus"
        :generating-ids="generatingIds"
        @change-status="(id, status) => emit('change-trainee-status', id, status)"
        @upload-certificate="emit('upload-certificate', $event)"
        @view-certificate="emit('view-certificate', $event)"
        @generate-certificate="emit('generate-certificate', $event)"
      />
    </section>

    <section
      data-testid="view-training-section-instructors"
      class="flex flex-col gap-4 border-b border-gray-02 px-6 py-8"
    >
      <TrainingViewOpenInstructorsList
        v-if="sectionsVariant === 'OPEN'"
        :instructors="training.instructors"
        :instructor-type="training.instructorType"
        :external-instructor-cnpj="training.externalInstructorCnpj"
        :external-instructor-company-name="training.externalInstructorCompanyName"
        variant="VIEW"
      />
      <TrainingViewInstructorsList
        v-else
        :instructors="training.instructors"
      />
    </section>

    <section
      v-if="sectionsVariant === 'OPEN'"
      data-testid="view-training-section-certificate"
      class="flex flex-col gap-4 border-b border-gray-02 px-6 py-8"
    >
      <h2 class="text-sm font-semibold text-gray-07">
        Necessário certificado?
      </h2>
      <TrainingViewCertificateFields
        :certificate="training.certificate"
        variant="VIEW"
      />
    </section>

    <section
      v-if="sectionsVariant === 'DEFAULT'"
      data-testid="view-training-section-documents"
      class="flex flex-col gap-4 border-b border-gray-02 px-6 py-8"
    >
      <h2 class="text-sm font-semibold text-gray-07">
        Documentos
      </h2>
      <TrainingDocumentsTable
        :documents="documentRows"
        :show-view-action="false"
        empty-message="Nenhum documento vinculado ao treinamento"
      />
    </section>

    <section
      data-testid="view-training-section-notes"
      class="flex flex-col gap-4 px-6 py-8"
    >
      <h2 class="text-sm font-semibold text-gray-07">
        Observações
      </h2>
      <p
        data-testid="view-training-notes"
        class="min-h-24 whitespace-pre-line rounded-2xl border border-border-color bg-gray-02/20 px-5 py-4 text-sm text-gray-07"
      >
        {{ training.notes ?? '' }}
      </p>
    </section>
  </div>
</template>
